import { useEffect, useMemo, useState } from "react";
import { FileSearch, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { Language } from "@/lib/finance";
import { MANUAL_SOURCE_FILTER_ID, MANUAL_SOURCE_LABEL } from "@/lib/manual-entry";
import type { ImportSource } from "@/lib/supabase-repository";

type SourceFilterProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: ImportSource[];
  selectedIds: string[];
  onApply: (ids: string[]) => void;
  language: Language;
  manualCount?: number;
};

export function SourceFilter({ open, onOpenChange, sources, selectedIds, onApply, language, manualCount = 0 }: SourceFilterProps) {
  const [draft, setDraft] = useState<string[]>(selectedIds);
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (open) setDraft(selectedIds);
  }, [open, selectedIds]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const options = [
      ...(manualCount > 0 ? [{ id: MANUAL_SOURCE_FILTER_ID, fileName: MANUAL_SOURCE_LABEL, fileFormat: "manual", rowCount: manualCount, importedByName: "Dashboard" }] : []),
      ...sources,
    ];
    return options.filter((source) => !query || [source.fileName, source.importedByName, source.fileFormat]
      .join(" ").toLowerCase().includes(query));
  }, [sources, search, manualCount]);
  const toggle = (id: string) => setDraft((current) => current.includes(id)
    ? current.filter((value) => value !== id)
    : [...current, id]);
  const text = language === "id" ? {
    title: "Filter sumber data",
    description: "Pilih satu atau beberapa file maupun Input Manual. Tabel, KPI, grafik, CSV, dan Excel akan mengikuti pilihan ini.",
    search: "Cari sumber",
    all: "Pilih semua hasil",
    clear: "Bersihkan",
    apply: "Terapkan filter",
    empty: "Belum ada sumber data.",
  } : {
    title: "Data source filter",
    description: "Choose one or more files or Manual Input. The table, KPIs, charts, CSV, and Excel will follow this selection.",
    search: "Search sources",
    all: "Select all results",
    clear: "Clear",
    apply: "Apply filter",
    empty: "There are no data sources yet.",
  };
  const filteredIds = filtered.map((source) => source.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => draft.includes(id));

  return <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
      <SheetHeader className="border-b px-4 py-5 sm:px-6"><SheetTitle className="flex items-center gap-2"><FileSearch className="size-5" />{text.title}</SheetTitle><SheetDescription>{text.description}</SheetDescription></SheetHeader>
      <div className="space-y-4 p-4 pb-2 sm:p-6 sm:pb-2">
        <div className="relative"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={text.search} className="min-h-11 pl-9" /></div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center"><Button variant="outline" size="sm" className="min-h-10 sm:min-h-8" disabled={!filtered.length} onClick={() => setDraft((current) => allFilteredSelected ? current.filter((id) => !filteredIds.includes(id)) : [...new Set([...current, ...filteredIds])])}><input type="checkbox" checked={allFilteredSelected} readOnly className="size-4 accent-fuchsia-600" />{text.all}</Button><Button variant="ghost" size="sm" className="min-h-10 sm:min-h-8" onClick={() => setDraft([])}>{text.clear}</Button><Badge variant="secondary" className="col-span-2 justify-center rounded-full sm:col-span-1 sm:ml-auto">{draft.length}</Badge></div>
        <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
          {filtered.map((source) => <label key={source.id} className="flex min-h-14 cursor-pointer items-start gap-3 rounded-xl border p-3 hover:bg-muted/40"><input type="checkbox" checked={draft.includes(source.id)} onChange={() => toggle(source.id)} className="mt-1 size-5 accent-fuchsia-600" /><span className="min-w-0"><span className="block truncate text-sm font-medium">{source.fileName}</span><span className="mt-1 block text-xs text-muted-foreground">{source.id === MANUAL_SOURCE_FILTER_ID ? (language === "id" ? "Dibuat dari dashboard" : "Created in dashboard") : source.fileFormat.toUpperCase()} · {source.rowCount} {language === "id" ? "baris" : "rows"}</span></span></label>)}
          {!filtered.length && <div className="flex min-h-36 flex-col items-center justify-center text-center text-muted-foreground"><FileSearch className="mb-3 size-6 opacity-40" /><p className="text-sm">{text.empty}</p></div>}
        </div>
      </div>
      <SheetFooter className="sticky bottom-0 border-t bg-background/95 px-4 py-4 backdrop-blur sm:px-6"><Button className="min-h-11 w-full" onClick={() => { onApply(draft); onOpenChange(false); }}>{text.apply}</Button></SheetFooter>
    </SheetContent>
  </Sheet>;
}
